// Working out the dates on a Processing record.
//
// A company's owner writes the RULES ("3 days before close", "5 days after
// the inspection period ends") in Board Setup; this module turns those
// rules plus one record's own dates into the list the timeline and the
// deadlines grid render. Pure — no database, no clock of its own — so it
// tests in the node pool and both the API and the UI can agree on one
// answer.
//
// Two ideas carry the whole design:
//
//   * A date is COMPUTED or USER_SET, never both. Computed dates fall out
//     of an anchor and an offset and are not editable — which is Nate's
//     "anything you control has a pencil" rule stated as data. It also
//     means a computed date can never drift out of agreement with the
//     contract date it derives from, because there is nowhere to store a
//     disagreeing value.
//
//   * An anchor may be another key date. That is what lets Insurance say
//     "document review is due 5 days after the policy documents arrive"
//     with no column for either date, and Realty say "the BINSR response
//     is due 5 days after the inspection period ends" when the inspection
//     period is itself computed. Chains are resolved by repeated passes
//     and bounded — see resolveKeyDates.
//
// A rule that cannot be worked out yields a null date and a reason, never
// a guess. A number the team sees is a promise (CLAUDE.md, accuracy):
// "we don't know yet" is an honest answer and a plausible-looking wrong
// date is not.

import { bankHolidayOn, dayOfWeek } from "../pay-runs/calendar";

/** How a definition arrives at its date. */
export type KeyDateResolution = "computed" | "user_set";

/** What a computed definition counts from. */
export type KeyDateAnchorKind = "record_date" | "key_date";

/**
 * The record's own dates a computed definition may anchor to. These are
 * the dates the OS already holds on a Processing record; a company's
 * setup screen offers exactly this list plus that company's own key dates.
 */
export const RECORD_DATE_ANCHORS = [
  /** When the Processing record opened. Every company has one. */
  "started",
  /** Contract acceptance — Realty's `under_contract_at`. */
  "acceptance",
  /** Realty's `listed_at`. */
  "listed",
  /** The close date in effect: the actual one once it exists, else the expected one. */
  "close",
  "expected_close",
  "actual_close",
  /** The end of the inspection / due-diligence window (`due_diligence_ends_at`). */
  "due_diligence_end",
  /**
   * The signing appointment — Mortgage's `mortgage_loans.signing_date`
   * (Joey's redesign, 2026-08-27). An anchor rather than a user_set key
   * date so the timeline and the loan record can never disagree: the loan
   * is the single home for this date, edited on the Loan details card.
   * Null for companies whose records carry no signing date.
   */
  "signing",
  /** When the record entered the stage it is in now. */
  "stage_entered",
] as const;

export type RecordDateAnchor = (typeof RECORD_DATE_ANCHORS)[number];

const RECORD_ANCHOR_SET = new Set<string>(RECORD_DATE_ANCHORS);

export function isRecordDateAnchor(value: string): value is RecordDateAnchor {
  return RECORD_ANCHOR_SET.has(value);
}

/** Plain-language names for the anchors, for the setup screen's picker. */
export const RECORD_DATE_ANCHOR_LABELS: Record<RecordDateAnchor, string> = {
  started: "the day this record opened",
  acceptance: "contract acceptance",
  listed: "the listing date",
  close: "close",
  expected_close: "the expected close date",
  actual_close: "the actual close date",
  due_diligence_end: "the end of the inspection period",
  signing: "the signing appointment",
  stage_entered: "the day it entered this stage",
};

export interface KeyDateDefinition {
  date_key: string;
  label: string;
  /** The rule in the words a person would say it in. Always displayed. */
  rule_text: string;
  resolution: KeyDateResolution;
  anchor_kind: KeyDateAnchorKind | null;
  anchor_key: string | null;
  offset_days: number;
  show_on_timeline: boolean;
  /** Draw a shaded band from that key date to this one. */
  band_from_key: string | null;
  sort_order: number;
}

/** One record's own dates, as ISO `YYYY-MM-DD` (or null when not on file). */
export type RecordAnchorDates = Partial<Record<RecordDateAnchor, string | null>>;

export type KeyDateStatus =
  /** The date is in the past. */
  | "passed"
  | "due_today"
  | "due_tomorrow"
  /** Within a week. */
  | "soon"
  | "upcoming"
  /** Somebody has to set this one and nobody has. */
  | "needs_date"
  /**
   * This loan doesn't need it — an appraisal waived by the lender, a
   * property with no HOA. Deliberately distinct from "needs_date": a
   * blank that means "handled" must never be counted with one that
   * means "nobody has done this yet".
   */
  | "not_applicable"
  /** A computed date whose anchor isn't on file yet. */
  | "unresolved";

export interface ResolvedKeyDate {
  date_key: string;
  label: string;
  rule_text: string;
  resolution: KeyDateResolution;
  /** True exactly when this is the kind of date a person sets — the pencil. */
  editable: boolean;
  /** Marked as not applying to this loan (with `unresolved_reason` as why). */
  not_applicable: boolean;
  /** Why it was removed, when someone said (or the OS worked it out). */
  not_applicable_reason?: string | null;
  /** ISO `YYYY-MM-DD`, or null when it cannot be worked out yet. */
  date: string | null;
  status: KeyDateStatus;
  /** Whole days from today; negative once past. null when there is no date. */
  days_away: number | null;
  /**
   * The day of the week or bank holiday when a date lands where an
   * office may be closed ("Sunday", "Thanksgiving Day"), else null.
   *
   * DECISION (Grant, 2026-11-05): a deadline is NEVER moved for a weekend
   * or a holiday. The Arizona purchase contract counts calendar days and a
   * day ends at 11:59 pm, so a Sunday deadline is a Sunday deadline. What
   * a person needs is to SEE that it is a Sunday, so the note rides beside
   * the date and the date itself is left exactly as the rule says.
   */
  falls_on?: string | null;
  /**
   * Why there is no date, in plain language — shown instead of a date so a
   * blank is never a mystery. null when `date` is set.
   */
  unresolved_reason: string | null;
  show_on_timeline: boolean;
  band_from_key: string | null;
  sort_order: number;
}

// ── ISO date arithmetic ───────────────────────────────────────────────
// Dates here are calendar days, not instants: "3 days before close" means
// three boxes back on a wall calendar. Doing the arithmetic in UTC keeps
// it exact (no DST-shortened day can round a date to its neighbour) while
// staying a pure string→string function.

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** The year/month/day of an ISO date, or null when it isn't a real date. */
function parseParts(value: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!value) return null;
  const match = ISO_DATE.exec(value.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const round = new Date(Date.UTC(y, m - 1, d));
  // Rejects the impossible (2026-02-31) rather than silently rolling it
  // forward into March, which is how a typo becomes a wrong deadline.
  if (
    round.getUTCFullYear() !== y ||
    round.getUTCMonth() !== m - 1 ||
    round.getUTCDate() !== d
  ) {
    return null;
  }
  return { y, m, d };
}

/** `YYYY-MM-DD` (from a date or a full timestamp), or null if unparseable. */
export function toIsoDate(value: string | null | undefined): string | null {
  const parts = parseParts(value);
  if (!parts) return null;
  return new Date(Date.UTC(parts.y, parts.m - 1, parts.d)).toISOString().slice(0, 10);
}

export function addDays(isoDate: string, days: number): string {
  const parts = parseParts(isoDate);
  if (!parts) return isoDate;
  return new Date(Date.UTC(parts.y, parts.m - 1, parts.d + days)).toISOString().slice(0, 10);
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number | null {
  const a = parseParts(from);
  const b = parseParts(to);
  if (!a || !b) return null;
  const ms = Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d);
  return Math.round(ms / 86_400_000);
}

/**
 * Today on the Arizona calendar — the calendar the advisor typing a date
 * is looking at. Same fixed UTC-7 shift renewal-anchor.ts and
 * automation-preview.ts already use (Arizona does not observe DST, so a
 * fixed offset is exact rather than an approximation).
 */
const ARIZONA_UTC_OFFSET_MS = 7 * 60 * 60 * 1000;

/**
 * "Saturday", "Sunday", a bank holiday's name, or null for an ordinary
 * weekday. The holidays are the Federal Reserve's (shared/pay-runs/calendar.ts
 * computes them, so there is one list in the OS, not a second typed one).
 */
export function closedDayName(isoDate: string): string | null {
  const parts = parseParts(isoDate);
  if (!parts) return null;
  const iso = isoDate.slice(0, 10);
  const holiday = bankHolidayOn(iso);
  if (holiday) return holiday;
  const dow = dayOfWeek(iso);
  if (dow === 0) return "Sunday";
  if (dow === 6) return "Saturday";
  return null;
}

export function arizonaToday(now: Date = new Date()): string {
  return new Date(now.getTime() - ARIZONA_UTC_OFFSET_MS).toISOString().slice(0, 10);
}

// ── Status ────────────────────────────────────────────────────────────

export function statusFor(daysAway: number): KeyDateStatus {
  if (daysAway < 0) return "passed";
  if (daysAway === 0) return "due_today";
  if (daysAway === 1) return "due_tomorrow";
  if (daysAway <= 7) return "soon";
  return "upcoming";
}

/**
 * The words the badge shows. Deliberately about the DATE, never about the
 * work: the OS knows a date has passed, it does not know the thing got
 * done, and a badge reading "Met" over an unmet deadline is exactly the
 * kind of quietly-wrong number the accuracy rule forbids.
 */
export function statusLabel(status: KeyDateStatus, daysAway: number | null): string {
  switch (status) {
    case "passed":
      return "Passed";
    case "due_today":
      return "Due today";
    case "due_tomorrow":
      return "Due tomorrow";
    case "soon":
      return daysAway === null ? "Soon" : `In ${daysAway} days`;
    case "upcoming":
      return "Upcoming";
    case "needs_date":
      return "Needs a date";
    case "not_applicable":
      return "Not applicable";
    case "unresolved":
      return "Waiting on another date";
  }
}

// ── Resolution ────────────────────────────────────────────────────────

export interface ResolveKeyDatesInput {
  definitions: KeyDateDefinition[];
  /** The record's own dates. */
  recordDates: RecordAnchorDates;
  /** Values advisors have set, keyed by date_key (user_set definitions). */
  userValues: Record<string, string | null>;
  /**
   * Dates the advisor has marked as not applying to this loan, keyed by
   * date_key, with the reason when one was given. A key present here
   * wins over any value: "not applicable" is an answer, not a blank.
   */
  notApplicable?: Record<string, { reason: string | null }>;
  /** Today, ISO. Injected so this stays pure and testable. */
  today: string;
}

/**
 * Lowercase a key-date label for embedding mid-sentence ("Waiting on
 * ___."), without breaking an acronym the label already carries in caps
 * (BINSR, SPDS, CDA, HOA, …). A plain `.toLowerCase()` turned the stored
 * label "BINSR response" into "Waiting on binsr response." — every word
 * lowercased, acronym included. Any word that's already ALL-CAPS in the
 * source label is assumed deliberate and left alone; everything else is
 * lowercased. Generalizes rather than hardcoding a word list, so a new
 * company's ALL-CAPS term (say, Insurance's "MVR") needs no code change.
 */
function lowercaseForSentence(label: string): string {
  return label
    .split(" ")
    .map((word) => (/^[A-Z]{2,}$/.test(word) ? word : word.toLowerCase()))
    .join(" ");
}

/**
 * Turn a company's rules plus one record's dates into the resolved list.
 *
 * Chained anchors are resolved by repeated passes: each pass settles every
 * definition whose anchor is already known, and the loop stops when a pass
 * settles nothing. Anything still unsettled is either waiting on a date
 * nobody has entered or sitting in a reference cycle — both report a null
 * date with a reason rather than a number. The pass count is bounded by
 * the number of definitions, so a cycle costs one wasted pass, not a hang.
 *
 * Returned in the company's configured order.
 */
export function resolveKeyDates(input: ResolveKeyDatesInput): ResolvedKeyDate[] {
  const { definitions, recordDates, userValues, today } = input;
  const notApplicable = input.notApplicable ?? {};

  const dates = new Map<string, string | null>();
  const reasons = new Map<string, string>();
  const settled = new Set<string>();

  // User-set dates settle immediately — there is nothing to compute.
  for (const def of definitions) {
    if (def.resolution !== "user_set") continue;
    const na = notApplicable[def.date_key];
    if (na) {
      // Marked as not applying: no date, and the reason stands in for
      // one. Settled here so nothing downstream tries to compute it.
      dates.set(def.date_key, null);
      settled.add(def.date_key);
      reasons.set(def.date_key, na.reason ?? "Marked as not applicable on this loan.");
      continue;
    }
    const value = toIsoDate(userValues[def.date_key] ?? null);
    dates.set(def.date_key, value);
    settled.add(def.date_key);
    if (!value) reasons.set(def.date_key, "Nobody has set this date yet.");
  }

  const byKey = new Map(definitions.map((def) => [def.date_key, def]));

  let changed = true;
  let passes = 0;
  while (changed && passes <= definitions.length) {
    changed = false;
    passes += 1;

    for (const def of definitions) {
      if (settled.has(def.date_key)) continue;
      if (def.resolution !== "computed" || !def.anchor_kind || !def.anchor_key) {
        // A row that claims to be computed but names nothing to count from
        // is malformed config, not a date. The schema's CHECK makes this
        // unreachable from the database; belt-and-suspenders for callers
        // that build definitions in memory.
        dates.set(def.date_key, null);
        reasons.set(def.date_key, "This rule doesn't say what to count from.");
        settled.add(def.date_key);
        changed = true;
        continue;
      }

      if (def.anchor_kind === "record_date") {
        const anchorLabel = isRecordDateAnchor(def.anchor_key)
          ? RECORD_DATE_ANCHOR_LABELS[def.anchor_key]
          : def.anchor_key;
        const anchorValue = toIsoDate(recordDates[def.anchor_key as RecordDateAnchor] ?? null);
        dates.set(def.date_key, anchorValue ? addDays(anchorValue, def.offset_days) : null);
        if (!anchorValue) {
          reasons.set(def.date_key, `Waiting on ${anchorLabel}, which isn't on this record yet.`);
        }
        settled.add(def.date_key);
        changed = true;
        continue;
      }

      // anchor_kind === "key_date" — resolvable only once its anchor is.
      const anchorDef = byKey.get(def.anchor_key);
      if (!anchorDef) {
        dates.set(def.date_key, null);
        reasons.set(def.date_key, "This rule counts from a date that no longer exists.");
        settled.add(def.date_key);
        changed = true;
        continue;
      }
      if (!settled.has(def.anchor_key)) continue; // try again next pass

      const anchorValue = dates.get(def.anchor_key) ?? null;
      dates.set(def.date_key, anchorValue ? addDays(anchorValue, def.offset_days) : null);
      if (!anchorValue) {
        reasons.set(def.date_key, `Waiting on ${lowercaseForSentence(anchorDef.label)}.`);
      }
      settled.add(def.date_key);
      changed = true;
    }
  }

  // Whatever is left could not settle: every remaining definition is
  // waiting on another that is itself waiting — a cycle.
  for (const def of definitions) {
    if (settled.has(def.date_key)) continue;
    dates.set(def.date_key, null);
    reasons.set(def.date_key, "These rules count from each other in a loop — check Board Setup.");
  }

  return definitions.map((def) => {
    // A date marked "not applicable" (removed from this file) shows no
    // date, whatever its rule would compute — the strip, the deadline
    // list and every automation reading these rows then drop it. Its
    // computed value stays in `dates` above, so a date that COUNTS FROM
    // it still resolves: removing "Inspection period ends" from the
    // screen must not quietly blank the BINSR response behind it
    // (Nate via Grant, 2026-09-22: remove deadlines a file will never
    // have; until then only user-set dates could be marked).
    const removed = Boolean(notApplicable[def.date_key]);
    const date = removed ? null : (dates.get(def.date_key) ?? null);
    const daysAway = date ? daysBetween(today, date) : null;
    const status: KeyDateStatus =
      date && daysAway !== null
        ? statusFor(daysAway)
        : removed
          ? "not_applicable"
          : def.resolution === "user_set"
            ? "needs_date"
            : "unresolved";
    return {
      date_key: def.date_key,
      label: def.label,
      rule_text: def.rule_text,
      resolution: def.resolution,
      editable: def.resolution === "user_set",
      not_applicable: removed,
      not_applicable_reason: removed ? (notApplicable[def.date_key]?.reason ?? null) : null,
      date,
      status,
      days_away: daysAway,
      falls_on: date ? closedDayName(date) : null,
      unresolved_reason: date ? null : (reasons.get(def.date_key) ?? null),
      show_on_timeline: def.show_on_timeline,
      band_from_key: def.band_from_key,
      sort_order: def.sort_order,
    };
  });
}

// ── The timeline ──────────────────────────────────────────────────────

export interface TimelineMarker {
  date_key: string;
  label: string;
  date: string;
  /** 0–100, where the marker sits along the drawn track. */
  position: number;
  status: KeyDateStatus;
}

export interface TimelineBand {
  from_key: string;
  to_key: string;
  label: string;
  start: number;
  end: number;
}

export interface ProcessingTimeline {
  /** The window the track spans. */
  start_date: string;
  end_date: string;
  markers: TimelineMarker[];
  bands: TimelineBand[];
  /** Where today sits, 0–100, or null when today is outside the window. */
  today_position: number | null;
  /** How much of the window has elapsed, 0–100 (clamped). */
  elapsed: number;
}

/**
 * Lay the timeline out from already-resolved dates.
 *
 * The track spans the first to the last dated marker, so it always fills
 * regardless of how long a record runs. A record with fewer than two dated
 * markers has no window to draw and returns null — the page shows the
 * deadlines list alone rather than a one-point line pretending to be a
 * timeline.
 */
export function buildTimeline(
  resolved: ResolvedKeyDate[],
  today: string,
): ProcessingTimeline | null {
  // Narrow to "has a date" once, so nothing below has to assert it again.
  const dated = resolved
    .flatMap((row) =>
      row.show_on_timeline && row.date ? [{ ...row, date: row.date }] : [],
    )
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.sort_order - b.sort_order));

  const first = dated[0];
  const last = dated[dated.length - 1];
  if (!first || !last || dated.length < 2) return null;

  const start = first.date;
  const end = last.date;
  const span = daysBetween(start, end);
  if (span === null || span <= 0) return null;

  const positionOf = (date: string): number => {
    const offset = daysBetween(start, date);
    if (offset === null) return 0;
    return Math.max(0, Math.min(100, (offset / span) * 100));
  };

  const markers: TimelineMarker[] = dated.map((row) => ({
    date_key: row.date_key,
    label: row.label,
    date: row.date,
    position: positionOf(row.date),
    status: row.status,
  }));

  const byKey = new Map(dated.map((row) => [row.date_key, row]));
  const bands: TimelineBand[] = [];
  for (const row of dated) {
    if (!row.band_from_key) continue;
    const from = byKey.get(row.band_from_key);
    if (!from?.date) continue;
    bands.push({
      from_key: from.date_key,
      to_key: row.date_key,
      label: `${from.label} → ${row.label}`,
      start: positionOf(from.date),
      end: positionOf(row.date),
    });
  }

  const todayOffset = daysBetween(start, today);
  const inWindow = todayOffset !== null && todayOffset >= 0 && todayOffset <= span;
  const rawElapsed = todayOffset === null ? 0 : (todayOffset / span) * 100;

  return {
    start_date: start,
    end_date: end,
    markers,
    bands,
    today_position: inWindow ? positionOf(today) : null,
    elapsed: Math.max(0, Math.min(100, rawElapsed)),
  };
}

// ─── Key dates as an automation anchor ───────────────────────────────
//
// The OS has always been able to WORK OUT when a contract deadline
// falls — that is what this module does — and has never been able to
// act on one. Every deadline below (BINSR response, loan contingency,
// close of escrow) was computed, rendered on the timeline, and then
// invisible to the automations engine, which had exactly one
// processing-deadline trigger (`when.processing.due_diligence`) hard-
// wired to the inspection window.
//
// That gap is most of what a transaction coordinator is hired to do:
// know what is due and chase it. So the dates are exposed to rules as
// a ctx namespace rather than as thirteen scalar fields —
//
//     ctx.days_until_key_date.binsr_response = 3
//
// — which the condition engine already navigates by dot-path
// (`getFieldFromCtx`), so no evaluator change was needed, and a company
// adding a fourteenth key date in Board Setup gets it as a rule anchor
// for free rather than waiting on a code change.
//
// Per CLAUDE.md's automation-first rule, this is deliberately only a
// TRIGGER: the OS notices the day. What to say, to whom, and whether to
// send at all is a rule a person builds, names, owns and can pause.

/** The ctx namespace holding "days until" for every resolvable key date. */
export const KEY_DATE_CTX_NAMESPACE = "days_until_key_date";

/** The condition field a key-date rule compiles to. */
export function keyDateConditionField(dateKey: string): string {
  return `${KEY_DATE_CTX_NAMESPACE}.${dateKey}`;
}

/** The date_key a condition field refers to, or null if it isn't one.
 *  The inverse of `keyDateConditionField`, so the compiler and the scan
 *  planner read the same field name the same way. */
export function keyDateFromConditionField(field: string): string | null {
  const prefix = `${KEY_DATE_CTX_NAMESPACE}.`;
  if (!field.startsWith(prefix)) return null;
  return field.slice(prefix.length) || null;
}

/**
 * The ctx namespace for one record: every key date that actually
 * resolved, as whole days from today (negative once past).
 *
 * A date that could NOT be worked out — nobody set it, or its anchor is
 * missing — is deliberately absent rather than present as null. A rule
 * asking "3 days before the appraisal deadline" must not fire on a deal
 * that has no appraisal deadline on file, and an absent key can never
 * equal a number, so the omission is the guard.
 */
export function keyDateDaysAway(resolved: ResolvedKeyDate[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of resolved) {
    if (k.date === null || k.days_away === null || k.not_applicable) continue;
    out[k.date_key] = k.days_away;
  }
  return out;
}
